<!--
  The calculation protocol — the actual order ("detaillierte Informationen wie alles berechnet
  wurde"). Steps in calculation order, candidates in evaluation order, per loser the reason.
  NOTHING is re-sorted and NOTHING is calculated: numbers arrive finished from the endpoint and are
  only formatted for German eyes here.

  Chip colours come from the backend's colour tokens (@UiEnumValueInfo) as an inline style —
  `severity` has no effect in this theme (primeng-v20-overrides.scss:6319 paints every p-tag
  primary blue). Reasoning in full in the class comment.
-->
<div class="cp-simulation-protocol">

    <!-- ── Messages of the calculation chain: the same codes and texts as in a productive run. -->
    @if (messages?.length) {
        <div class="cp-sim-messages mb-4">

            <h3 class="text-base font-semibold mt-0 mb-2">
                Meldungen der Rechenkette
            </h3>

            @for (message of messages; track $index) {
                <div class="cp-sim-message border-round p-3 mb-2"
                     [ngClass]="messageSeverityClass(message)">

                    <div class="flex align-items-start gap-2">
                        <!-- Severity caption from severityInfo; the colour class is decided on the FE enum. -->
                        @if (severityLabel(message)) {
                            <span class="text-xs font-semibold uppercase white-space-nowrap mt-1">
                                {{ severityLabel(message) }}
                            </span>
                        }
                        <span class="font-semibold white-space-nowrap">{{ message.code }}</span>
                        <span class="flex-1">{{ message.message }}</span>
                    </div>

                    @if (message.detail) {
                        <div class="text-sm mt-1">{{ message.detail }}</div>
                    }
                </div>
            }
        </div>
    }

    <!-- ── Protocol header -->
    <div class="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">

        <h3 class="text-base font-semibold m-0">
            Wie es berechnet wurde
            @if (steps?.length) {
                <span class="text-color-secondary font-normal text-sm ml-2">
                    {{ steps.length }} Schritte in Rechenreihenfolge
                </span>
            }
        </h3>

        @if (anyCandidates) {
            <div class="flex gap-2">
                <p-button label="Alle Kandidaten aufklappen"
                          icon="fa-solid fa-chevron-down"
                          severity="secondary"
                          size="small"
                          [text]="true"
                          (onClick)="handleExpandAllCandidates()">
                </p-button>
                <p-button label="Alle zuklappen"
                          icon="fa-solid fa-chevron-up"
                          severity="secondary"
                          size="small"
                          [text]="true"
                          (onClick)="handleCollapseAllCandidates()">
                </p-button>
            </div>
        }
    </div>

    @if (!steps?.length) {
        <!-- A result without protocol steps is a statement, not an empty page. -->
        <p-message severity="info"
                   text="Die Rechenkette hat für diese Eingaben keinen Protokollschritt gemeldet. Die Begründung steht in den Meldungen und im Zustand oberhalb.">
        </p-message>
    }

    <!-- ── The steps, in exactly the delivered order. -->
    @for (step of steps; track $index) {

        <div class="cp-sim-step surface-card border-round p-3 mb-3">

            <!-- Header: number, kind, title, key date, the Provisionssatz it belongs to -->
            <div class="flex flex-wrap align-items-center gap-2 mb-2">

                <span class="cp-sim-step__index text-color-secondary font-semibold">
                    {{ $index + 1 }}
                </span>

                @if (step.kindInfo?.label) {
                    <p-tag [value]="step.kindInfo.label"
                           [icon]="step.kindInfo.image"
                           [style]="chipStyle(step.kindInfo.backgroundColor, step.kindInfo.color)"
                           styleClass="cp-sim-tag">
                    </p-tag>
                }

                <span class="font-semibold flex-1">{{ step.title }}</span>

                @if (step.keyDate) {
                    <span class="text-sm text-color-secondary white-space-nowrap"
                          pTooltip="Stichtag, gegen den dieser Schritt geprüft hat"
                          tooltipPosition="top">
                        <i class="fa-regular fa-calendar mr-1"></i>{{ step.keyDate | date: 'dd.MM.yyyy' }}
                    </span>
                }

                @if (entitlementLabelOf(step)) {
                    <p-tag [value]="entitlementLabelOf(step)"
                           icon="fa-regular fa-file-invoice-dollar"
                           styleClass="cp-sim-tag cp-sim-tag--entitlement"
                           pTooltip="Dieser Schritt gehört zu diesem Provisionssatz"
                           tooltipPosition="top">
                    </p-tag>
                } @else {
                    <span class="text-xs text-color-secondary white-space-nowrap"
                          pTooltip="Gilt für den ganzen Vertrag, nicht für einen einzelnen Provisionssatz"
                          tooltipPosition="top">
                        vertragsweit
                    </span>
                }
            </div>

            <!-- Verdict of the step -->
            @if (step.verdict) {
                <div class="mb-2">{{ step.verdict }}</div>
            }

            <!-- Winner -->
            @if (step.winnerLabel) {
                <div class="cp-sim-winner border-round p-2 mb-2 flex align-items-start gap-2">
                    <i class="fa-solid fa-circle-check mt-1"></i>
                    <div>
                        <div class="font-semibold">{{ step.winnerLabel }}</div>
                        @if (step.winnerId != null) {
                            <div class="text-xs text-color-secondary">Id {{ step.winnerId }}</div>
                        }
                    </div>
                </div>
            }

            <!-- Figures of the step: the numbers the calculation used -->
            @if (step.figures?.length) {
                <div class="cp-sim-figures grid mb-2">
                    @for (figure of step.figures; track $index) {
                        <div class="col-12 md:col-6 lg:col-4 py-1">
                            <div class="text-xs text-color-secondary">{{ figure.label }}</div>
                            <div class="font-semibold">{{ formatFigure(figure) }}</div>
                        </div>
                    }
                </div>
            }

            <!-- Explanation, usually the formula in words -->
            @if (step.detail) {
                <div class="text-sm text-color-secondary mb-2">{{ step.detail }}</div>
            }

            <!-- Candidates: the core of the order. Collapsed, but complete. -->
            @if (step.candidates?.length) {

                <!--
                  The label carries the breakdown by verdict — it is the only thing a collapsed
                  step with 91 candidates says at all. Numbers AND captions arrive finished in
                  `step.verdictCounts`; without the field (older backend) it is the plain total.
                -->
                <p-button
                        [label]="candidateToggleLabel(step, isCandidatesExpanded($index))"
                        [icon]="isCandidatesExpanded($index) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
                        severity="secondary"
                        size="small"
                        [text]="true"
                        (onClick)="handleToggleCandidates($index)">
                </p-button>

                @if (isCandidatesExpanded($index)) {
                    <div class="cp-sim-candidates mt-2 overflow-x-auto">

                        <p-table [value]="step.candidates"
                                 [paginator]="step.candidates.length > candidatePageSize"
                                 [rows]="candidatePageSize"
                                 [rowsPerPageOptions]="[25, 50, 100]"
                                 currentPageReportTemplate="{first} bis {last} von {totalRecords} Kandidaten"
                                 [showCurrentPageReport]="true"
                                 styleClass="p-datatable-sm">

                            <ng-template #header>
                                <tr>
                                    <th class="w-3rem">#</th>
                                    <th class="w-6rem">Priorität</th>
                                    <th>Bezeichnung</th>
                                    <th class="w-12rem">Urteil</th>
                                    <th>Grund</th>
                                    <th class="w-14rem">Gültigkeit</th>
                                    <th class="w-14rem">Matrix / Achse</th>
                                </tr>
                            </ng-template>

                            <ng-template #body let-candidate let-rowIndex="rowIndex">
                                <tr>
                                    <td class="text-color-secondary">{{ rowIndex + 1 }}</td>

                                    <td>
                                        @if (candidate.orderPriority != null) {
                                            {{ candidate.orderPriority }}
                                        } @else {
                                            <span class="text-color-secondary">—</span>
                                        }
                                    </td>

                                    <td>
                                        <div>{{ candidate.label }}</div>
                                        @if (candidate.id != null) {
                                            <div class="text-xs text-color-secondary">Id {{ candidate.id }}</div>
                                        }
                                    </td>

                                    <td>
                                        @if (candidate.verdictInfo?.label) {
                                            <p-tag [value]="candidate.verdictInfo.label"
                                                   [icon]="candidate.verdictInfo.image"
                                                   [style]="chipStyle(candidate.verdictInfo.backgroundColor, candidate.verdictInfo.color)"
                                                   styleClass="cp-sim-tag">
                                            </p-tag>
                                        }
                                    </td>

                                    <td class="text-sm">{{ candidate.reason }}</td>

                                    <td class="text-sm white-space-nowrap">{{ validityOf(candidate) }}</td>

                                    <td class="text-sm">
                                        @if (candidate.matrixName) {
                                            <div>{{ candidate.matrixName }}</div>
                                        }
                                        @if (axisOf(candidate)) {
                                            <div class="text-xs text-color-secondary">{{ axisOf(candidate) }}</div>
                                        }
                                        @if (!candidate.matrixName && !axisOf(candidate)) {
                                            <span class="text-color-secondary">—</span>
                                        }
                                    </td>
                                </tr>
                            </ng-template>

                        </p-table>
                    </div>
                }
            }

        </div>
    }

    <!--
      K-17 "Berechnete Berater": measured, the service NEVER emits a row for this step. A step that
      is silently missing reads like a forgotten step — so its absence is stated. Only after
      COMPLETED: after NOT_ELIGIBLE or NO_EVENT the chain never gets there, and "no rule fired"
      would be an answer this run does not have.
    -->
    @if (steps?.length && showCalculatedAgentsNote) {
        <div class="cp-sim-step cp-sim-step--empty surface-card border-round p-3 mb-3">
            <div class="flex align-items-center gap-2">
                <i class="fa-regular fa-diagram-project text-color-secondary"></i>
                <span class="font-semibold">Berechnete Berater (K-17)</span>
            </div>
            <div class="text-sm text-color-secondary mt-1">
                Keine Regel hat zusätzliche Berater abgeleitet.
            </div>
        </div>
    }

</div>
